import { type RefObject, useLayoutEffect, useRef } from 'react'

export type CssVars = object
type CssTarget = HTMLElement | SVGElement

// CSSOM setProperty requires HYPHENATED property names and SILENTLY ignores
// camelCase — so `pointerEvents`/`gridTemplateColumns`-style keys were
// dropped without error while single-word keys applied. That shipped an
// invisible click shield: Grain's `pointerEvents: 'none'` never applied, and
// the absolutely-positioned overlay swallowed every click on surfaces using
// it (evaluation wizard options unselectable — 2026-08-20 incident).
// Callers write camelCase (React style convention); convert here. Real
// custom properties (`--foo`) pass through untouched.
function toCssPropertyName(name: string): string {
  if (name.startsWith('--')) return name
  return name.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)
}

function applyCssVars(element: CssTarget | null, vars?: CssVars) {
  if (!element || !vars) return

  Object.entries(vars).forEach(([name, value]) => {
    const cssName = toCssPropertyName(name)
    if (value === null || value === undefined) {
      element.style.removeProperty(cssName)
      return
    }
    if (typeof value !== 'string' && typeof value !== 'number') return
    element.style.setProperty(cssName, String(value))
  })
}

export function useApplyCssVars<TElement extends CssTarget>(
  ref: RefObject<TElement | null>,
  vars?: CssVars,
) {
  useLayoutEffect(() => {
    applyCssVars(ref.current, vars)
  }, [ref, vars])
}

export function useCssVars<TElement extends CssTarget>(vars?: CssVars) {
  const ref = useRef<TElement | null>(null)

  useLayoutEffect(() => {
    applyCssVars(ref.current, vars)
  }, [vars])

  return ref
}
